import { useTheme, Box, Stack, Typography, alpha, Divider } from '@mui/material'
import { Bar, Scatter } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'
import { max, min, range } from 'lodash'
import { useAppSelector } from '../../store/hooks'
import { roundUpToNearest10 } from '../../shared/utils'
import { fsColors } from '../../shared/constants'

const ChartKPIE = ({ results, height = 320 }: { results: any; height?: number }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const labels = results.map((rrr: any) => t(rrr.label.text))

  let annotations = {}
  range(0, labels.length - 1).forEach((_: any, index: number) => {
    annotations = {
      ...annotations,
      [`lineAt${index}`]: {
        type: 'line',
        scaleID: 'x',
        value: index + 0.5,
        endValue: index + 0.5,
        borderColor: alpha(theme.palette.text.secondary, 0.2),
        borderWidth: 2,
        borderDash: [20, 40],
      },
    }
  })

  return (
    <Box pt={4} pb={1.5}>
      <Stack direction="row" alignItems="center" px={4}>
        <Box width={75}>
          <Typography fontWeight="bold" mt={9}>{t(results[0].value[0].label)}</Typography>
        </Box>
        <Box height={height} width="calc(100% - 75px)">
          <Bar
            className="female_bar"
            id="female_bar"
            data={{
              labels,
              datasets: [
                {
                  label: t(results[0].value[0].label),
                  data: results.map((rrr: any) => rrr.value[0].value),
                  backgroundColor: fsColors.fsPurple.colors[0],
                  barThickness: 50,
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              layout: {
                padding: 0,
              },
              plugins: {
                // @ts-ignore
                legendMargin: false,
                legend: {
                  display: false,
                },
                tooltip: {
                  enabled: true,
                  callbacks: {
                    label: (context) => {
                      return `${t('proportion.of')} ${
                        context.dataset.label
                        // @ts-ignore
                      }: ${context.raw.toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`
                    },
                  },
                },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  padding: 4,
                  color: theme.palette.text.primary,
                  formatter: (value) =>
                    `${Number(value).toLocaleString(currentLanguage, {
                      maximumFractionDigits: 2,
                    })} %`,
                },
                annotation: {
                  clip: false,
                  annotations,
                },
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                  },
                  border: {
                    color: theme.palette.text.disabled,
                  },
                  ticks: {
                    display: false,
                  },
                  offset: true,
                  type: 'category',
                },
                xTop: {
                  position: 'top',
                  grid: {
                    display: false,
                  },
                  border: {
                    display: false,
                  },
                  ticks: {
                    callback: (value: any) => {
                      const maxWidth = 20 // max chars per line
                      const words = labels[value].split(' ')
                      const lines = []
                      let currentLine = ''

                      words.forEach((word: string) => {
                        if ((currentLine + word).length > maxWidth) {
                          lines.push(currentLine.trim())
                          currentLine = `${word} `
                        } else {
                          currentLine = `${currentLine} ${word} `
                        }
                      })
                      lines.push(currentLine.trim())

                      return lines
                    },
                    align: 'center',
                    color: theme.palette.text.primary,
                    font: {
                      lineHeight: 1.5,
                      weight: 'bold',
                    },
                  },
                  offset: true,
                  type: 'category',
                },
                y: {
                  type: 'linear',
                  grid: {
                    display: true,
                    color: theme.palette.divider,
                  },
                  border: {
                    color: theme.palette.text.disabled,
                  },
                  ticks: {
                    autoSkip: true,
                    stepSize: 10,
                    maxTicksLimit: 8,
                    color: theme.palette.text.secondary,
                    callback: (value) =>
                      `${Number(value).toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`,
                  },
                },
              },
            }}
          />
        </Box>
      </Stack>
      <div
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(0,0,0,0.2) 33%, rgba(255, 255, 255, 0) 0%)',
          backgroundPosition: 'center bottom',
          backgroundSize: '60px 2px',
          backgroundRepeat: 'repeat-x',
          height: '2px',
          marginLeft: 70,
          marginRight: 16,
        }}
      />
      <Stack direction="row" alignItems="center" px={4}>
        <Box width={75}>
          <Typography fontWeight="bold" mb={9}>{t(results[0].value[1].label)}</Typography>
        </Box>
        <Box height={height} width="calc(100% - 75px)">
          <Bar
            className="male_bar"
            id="male_bar"
            data={{
              labels,
              datasets: [
                {
                  label: t(results[0].value[1].label),
                  data: results.map((rrr: any) => rrr.value[1].value),
                  backgroundColor: fsColors.fsGray.colors[1],
                  barThickness: 50,
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              layout: {
                padding: 0,
              },
              plugins: {
                // @ts-ignore
                legendMargin: false,
                legend: {
                  display: false,
                },
                tooltip: {
                  enabled: true,
                  callbacks: {
                    label: (context) => {
                      return `${t('proportion.of')} ${
                        context.dataset.label
                        // @ts-ignore
                      }: ${context.raw.toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`
                    },
                  },
                },
                datalabels: {
                  anchor: 'end',
                  align: 'top',
                  padding: 4,
                  color: theme.palette.text.primary,
                  formatter: (value) =>
                    `${Number(value).toLocaleString(currentLanguage, {
                      maximumFractionDigits: 2,
                    })} %`,
                },
                annotation: {
                  clip: false,
                  annotations,
                },
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                  },
                  border: {
                    color: theme.palette.text.disabled,
                  },
                  ticks: {
                    callback: (value: any) => {
                      const maxWidth = 20 // max chars per line
                      const words = labels[value].split(' ')
                      const lines = []
                      let currentLine = ''

                      words.forEach((word: string) => {
                        if ((currentLine + word).length > maxWidth) {
                          lines.push(currentLine.trim())
                          currentLine = `${word} `
                        } else {
                          currentLine = `${currentLine} ${word} `
                        }
                      })
                      lines.push(currentLine.trim())

                      return lines
                    },
                    align: 'center',
                    color: theme.palette.text.primary,
                    font: {
                      lineHeight: 1.5,
                      weight: 'bold',
                    },
                  },
                  offset: true,
                  type: 'category',
                },
                y: {
                  type: 'linear',
                  grid: {
                    display: true,
                    color: theme.palette.divider,
                  },
                  border: {
                    color: theme.palette.text.disabled,
                  },
                  ticks: {
                    autoSkip: true,
                    stepSize: 10,
                    maxTicksLimit: 8,
                    color: theme.palette.text.secondary,
                    callback: (value) =>
                      `${Number(value).toLocaleString(currentLanguage, {
                        maximumFractionDigits: 2,
                      })} %`,
                  },
                },
              },
            }}
          />
        </Box>
      </Stack>
    </Box>
  )
}

export default ChartKPIE
